import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

const data = [
  {
    key: 1,
    name: "Yoga",
    description: "Traditional asanas for body & mind harmony",
    icon: "🧘‍♀️",
    img: "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?q=80&w=1000&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxleHBsb3JlLWZlZWR8Mnx8fGVufDB8fHx8fA%3D%3D",
    color: "from-sage-400 to-teal-400",
  },
  {
    key: 2,
    name: "Meditation",
    description: "Find peace through mindfulness practice",
    icon: "🕉️",
    img: "https://www.hindustantimes.com/ht-img/img/2023/05/21/550x309/_0623df8a-ca39-11e9-9a71-0afbe126130e_1684645951004.jpg",
    color: "from-teal-400 to-lavender-400",
  },
  {
    key: 3,
    name: "Zumba",
    description: "Dance your way to fitness & fun",
    icon: "💃",
    img: "https://www.verywellfit.com/thmb/SaUyT2h2ujEDx4zCAU0ilFclWqI=/1500x0/filters:no_upscale():max_bytes(150000):strip_icc()/4688722-GettyImages-950806258-06e1e050ab184f3694fd96017c9a42ee.jpg",
    color: "from-earth-400 to-earth-500",
  },
  {
    key: 4,
    name: "Aerobics",
    description: "High-energy cardio workouts",
    icon: "🏃‍♀️",
    img: "https://media.istockphoto.com/id/841069776/photo/happy-people-in-an-aerobics-class-at-the-gym.jpg?s=612x612&w=0&k=20&c=Msbb_TNBDZWWZfnuaZubcgE7Qa-qimYrl4D3aFQv9PY=",
    color: "from-sage-500 to-sage-600",
  },
  {
    key: 5,
    name: "Dance",
    description: "Express yourself through movement",
    icon: "🎭",
    img: "https://images.squarespace-cdn.com/content/v1/5ac09f2bc3c16a6451f0ce9c/1660717725757-N2EPJZHSGIV08HDV5QI6/IMG_3812.jpg?format=2500w",
    color: "from-earth-300 to-earth-400",
  },
  {
    key: 6,
    name: "Healing",
    description: "Restore balance & natural wellness",
    icon: "✨",
    img: "https://www.verywellmind.com/thmb/SjHXo0enMwOea-BQyWZPYnLT730=/1500x0/filters:no_upscale():max_bytes(150000):strip_icc()/VWM-reiki-selcare-getty-A-7b7532f06161455e90e950807cb705d3.jpg",
    color: "from-lavender-400 to-lavender-500",
  },
  {
    key: 7,
    name: "Naturopathy",
    description: "Natural remedies for holistic health",
    icon: "🌿",
    img: "https://img.freepik.com/free-photo/variety-spices-are-cutting-board-with-green-vegetable-background_1340-23417.jpg",
    color: "from-sage-400 to-sage-500",
  },
  {
    key: 8,
    name: "Massage",
    description: "Therapeutic touch for deep relaxation",
    icon: "💆‍♀️",
    img: "https://hips.hearstapps.com/hmg-prod/images/spa-woman-female-enjoying-massage-in-spa-centre-royalty-free-image-492676582-1549988720.jpg",
    color: "from-teal-300 to-teal-400",
  },
];

const Services = () => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  return (
    <section id="services" className="py-20 lg:py-32 relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-sage-200 rounded-full blur-3xl opacity-20"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-earth-200 rounded-full blur-3xl opacity-20"></div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-2 bg-sage-100 text-sage-700 rounded-full text-sm font-semibold mb-4">
            What We Offer
          </span>
          <h2 className="section-title">Our Services</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Discover a wide range of wellness services designed to nurture your
            body, mind, and spirit
          </p>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {data.map((service) => (
            <motion.div
              key={service.key}
              variants={itemVariants}
              className="group"
            >
              <div className="relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                {/* Image Container */}
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={service.img}
                    alt={service.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  {/* Gradient Overlay */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-t ${service.color} opacity-0 group-hover:opacity-90 transition-opacity duration-300`}
                  ></div>

                  {/* Icon Badge */}
                  <div className="absolute top-4 right-4 w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-2xl">{service.icon}</span>
                  </div>

                  {/* Hover Buttons */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 p-6">
                    <button className="w-full px-6 py-3 bg-white text-gray-900 font-semibold rounded-full hover:bg-gray-100 transform hover:scale-105 transition-all duration-200 shadow-lg">
                      Register
                    </button>
                    <button className="w-full px-6 py-3 bg-transparent text-white font-semibold rounded-full border-2 border-white hover:bg-white hover:text-gray-900 transform hover:scale-105 transition-all duration-200">
                      Book Demo
                    </button>
                  </div>
                </div>

                {/* Card Content */}
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {service.name}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {service.description}
                  </p>

                  {/* Decorative Line */}
                  <div
                    className={`mt-4 h-1 w-12 bg-gradient-to-r ${service.color} rounded-full`}
                  ></div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-16"
        >
          <p className="text-lg text-gray-600 mb-6">
            Not sure which service is right for you?
          </p>
          <button
            onClick={() => {
              const element = document.getElementById("contact");
              if (element) element.scrollIntoView({ behavior: "smooth" });
            }}
            className="btn-primary"
          >
            Schedule a Free Consultation
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
